import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Lottie from "lottie-react";

import shareIcon from "../../../assets/images/share.png";
import closeIcon from "../../../assets/images/close.png";
import LottieAnimation from "../../../assets/json/sezione-05-popup-01-en.json";
import arrowIconWhite from "../../../assets/images/arrowIconWhite.png";

/* ===================== DATI CONTENUTI ===================== */
const INPUT_CONTENT = {
  Financial: [
    "Total premium income",
    "Asset under Management",
    "Integration of ESG criteria in investment processes",
  ],
  Production: [
    "Agency network",
    "Headquarters",
    "Claims settlement offices",
    "Brokers, intermediaries and financial consultants",
    "Home banking",
    "Bank branches and Reale Bank spaces",
    "Real estate property"
  ],
  Intellectual: [
    "Corporate and technology know-how",
    "Digitalisation of Agency Factory",
    "Reale Group Digital Factory",
    "Corporate Venturing, Ecosystems and Partnerships and Reale Lab 1828",
    "Investments in sustainable innovation AI",
  ],
  Human: [
    "Group employees",
    "Employee benefits",
    "Group Academy training school",
    "Periodic performance evaluation and professional development",
    "Accessibility",
    "Organisational culture of diversity and inclusion",
  ],
  "Social/Relational": [
    "Members/Policyholders-Customers",
    "Suppliers and partners",
    "Initiatives to improve disadvantaged group's access to financial services",
    "Employee volunteering schemes",
    "Donation and sponsorships",
    "Institutional relationships",
    "Impact philanthropy initiatives of Reale Foundation",
  ],
  Natural: [
    "Energy consumption monitoring",
    "Energy saving actions",
    "CO₂ emission monitoring",
    "Action plan to reduce direct environmental impacts",
    "GHG emission mitigation and offsetting plans",
    "Environmental management system",
  ],
};

const OUTPUT_CONTENT = {
  Financial: [
    "Consolidated profit",
    "Distributed value",
    "Sustainable investments",
    "Solvency ratio",
    "Mutual advantages",
  ],
  Production: [
    "Technical excellence",
    "Products with environmental and/or social characteristics",
    "Swift response to new remote operational needs of Members/Policyholders and Customers",
    "Continuity of service",
    "LEED Gold and Platinum property certificates in Turin and Gold and Silver certificates for the Spanish offices",
  ],
  Intellectual: [
    "Process efficiency",
    "Innovative partnerships and eco-system development",
    "Structured redesign of working methods",
  ],
  Human: [
    "Employee well-being",
    "Inclusion & Employer Branding",
    "Promotion of diversity and inclusion",
    "Safe and accessible working environment",
    "Employer reputation",
    "Trust Index",
  ],
  "Social/Relational": [
    "Corporate reputation",
    "Sustainability Index",
    "Customer satisfaction",
    "Impact measurement",
    "Measurement of social return on investments - SROI",
  ],
  Natural: [
    "Carbon footprint",
    "Reduction of consumption",
    "Efficient waste management",
    "Reduction and offsetting of emissions",
    "UNI EN ISO 14001 certification",
  ],
};

/* ===================== COMPONENTI UI ===================== */

/** Stack verticale dei toggle (INPUT/OUTPUT) */
function ToggleStack({ section = "input", activeKey, onOpen, label }) {
  const KEYS = section === "input" ? Object.keys(INPUT_CONTENT) : Object.keys(OUTPUT_CONTENT);

  return (
    <div
      className="
        pointy-box  pointy-wide
        mb-8
        flex flex-col gap-1 border-[2px] border-[#c66529]
        p-4 md:p-8
      "
      style={{ borderRadius: "15px 15px 50px 50px" }}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      {KEYS.map((k) => {
        const isOpen = activeKey === k;
        return (
          <button
            key={k}
            type="button"
            aria-expanded={isOpen}
            onClick={(e) => onOpen(isOpen ? null : { key: k, el: e.currentTarget })}
            className="
              flex items-center justify-between gap-3
              text-[#0E3761] text-[12px] md:text-[26px]
              hover:opacity-90 transition
            "
          >
            <span>{k}</span>
            <span>{isOpen ? "–" : "+"}</span>
          </button>
        );
      })}
      <p className="text-center text-[14px] md:text-[30px] text-[#c66529] font-bold">{label}</p>
    </div>
  );
}

/** Pannello flottante arancione ancorato sopra al toggle cliccato */
function FloatingPanel({ title, items = [], onClose, anchorY }) {
  return (
    <div
      className="absolute left-1/2 -translate-x-1/2 z-[5] w-[92%] max-w-[620px]"
      style={{ top: anchorY }} // <-- allineamento al bordo superiore del filetto
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <div
        className="relative bg-[#c66529] text-white rounded-sm shadow-lg px-5 md:px-8 py-5"
        role="dialog"
        aria-label={title}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-2 text-white/90 text-xl leading-none"
          aria-label="Close panel"
          title="Close"
        >
          –
        </button>

        {/* TITOLETTO */}
        <div className="text-left text-[12px] md:text-[20px] font-bold mb-3">
          {title}
        </div>

        {/* TESTO allineato a sinistra + interlinea ridotta */}
        <div className="text-left text-[10px] md:text-[18px] leading-[1.15] whitespace-pre-line">
          {items.map((t, i) => (
            <div key={i} className="mb-2 last:mb-0">
              {t}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ===================== POPUP ===================== */

export default function ModelloBusiness({ isOpen, onClose }) {
  const overlayRef = useRef(null);
  const stageRef = useRef(null);

  const inputBoxRef = useRef(null);
  const outputBoxRef = useRef(null);

  const [panel, setPanel] = useState(null);

  // ESC + blocco scroll quando il popup è aperto
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose?.();
    if (isOpen) {
      document.body.classList.add("overflow-hidden");
      window.addEventListener("keydown", onKey);
    }
    return () => {
      document.body.classList.remove("overflow-hidden");
      window.removeEventListener("keydown", onKey);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const onShare = (popupId) => {
    const url = new URL(window.location.href);
    url.searchParams.set("popup", popupId);

    if (navigator.share) {
      navigator
        .share({ title: "Business Model", url: url.toString() })
        .catch(() => {});
    } else if (navigator.clipboard) {
      navigator.clipboard.writeText(url.toString()).catch(() => {});
    }
  };

  // calcola e apre pannello ancorato alla voce cliccata
  const openFrom = (section) => (payload) => {
    if (!payload) return setPanel(null);
    const { key } = payload;

    const stageEl = stageRef.current;
    const refBox = section === "input" ? inputBoxRef.current : outputBoxRef.current;

    let anchorY = 140; // fallback
    if (stageEl && refBox) {
      const boxRect = refBox.getBoundingClientRect();
      const stageRect = stageEl.getBoundingClientRect();
      // distanza del bordo superiore del “filetto” rispetto al top dello stage
      anchorY = boxRect.top - stageRect.top;
    }

    setPanel({ section, key, anchorY });
  };

  const activeItems =
    panel?.section === "input"
      ? INPUT_CONTENT[panel?.key || ""]
      : OUTPUT_CONTENT[panel?.key || ""];

  return createPortal(
    <div
      ref={overlayRef}
      className="fixed inset-0 z-[1001] bg-black/50 backdrop-blur-[2px] flex items-center justify-center p-3 md:p-6"
      onMouseDown={(e) => {
        if (e.target === overlayRef.current) onClose?.();
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Business Model"
    >
      <div
        className="
          relative w-full md:w-[1280px] bg-[#E7E3DF] p-6 md:p-8
          max-h-[90vh] overflow-y-auto overscroll-contain
        "
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Azioni in alto */}
        <div className="sticky top-3 z-10 flex items-center gap-2 justify-end pr-3">
          <button
            type="button"
            onClick={() => onShare("ModelloBusiness")}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full text-[#0E3761] bg-white/90 border border-[#0E3761]/30 hover:bg-white transition"
            title="Share"
            aria-label="Share"
          >
            <img src={shareIcon} alt="" className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onClose?.();
            }}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full text-[#0E3761] bg-white/90 border border-[#0E3761]/30 hover:bg-white transition"
            title="Close"
            aria-label="Close"
          >
            <img src={closeIcon} alt="Close" className="h-4 w-4" />
          </button>
        </div>

        <h2 className="text-[#c66529] text-[30px] md:text-[40px] font-bold">
          Business Model
        </h2>

        {/* Contenuto */}
        <div className="pt-24 pb-8 md:pt-14">
          {/* STAGE RELATIVO: il pannello si posiziona rispetto a questo wrapper */}
          <div
            ref={stageRef}
            className="relative flex flex-col items-center gap-5 max-w-[1080px] mx-auto"
          >
            {/* Toggle INPUT (sopra al cerchio) */}
            <div ref={inputBoxRef}>
              <ToggleStack
                section="input"
                activeKey={panel?.section === "input" ? panel.key : null}
                onOpen={openFrom("input")}
                label="INPUT"
              />
            </div>

            {/* Lottie */}
            <Lottie
              animationData={LottieAnimation}
              loop={false}
              autoplay
              className="w-full h-auto"
            />

            {/* Toggle OUTPUT (sotto al cerchio) */}
            <div ref={outputBoxRef}>
              <ToggleStack
                section="output"
                activeKey={panel?.section === "output" ? panel.key : null}
                onOpen={openFrom("output")}
                label="OUTPUT"
              />
            </div>

            {/* Pannello flottante */}
            {panel && activeItems?.length > 0 && (
              <FloatingPanel
                title={panel.key}
                items={activeItems}
                anchorY={panel.anchorY}
                onClose={() => setPanel(null)}
              />
            )}
          </div>
        </div>

        {/* CTA finale */}
        <div className="mt-4">
          <a href="https://www.realegroup.eu/groupshare/0/EN/ListaReportsAndAccounts/64/Reale%20Group_Integrated%20Consolidated%20Financial%20Statement%202025.pdf#page=15" target="_blank">
            <button
              type="button"
              className="bt-secondary2 text-sm px-4 py-2"
            >
              <img
                src={arrowIconWhite}
                alt="Arrow"
                className="inline-block mr-2 w-[20px] h-[20px]"
              />
              Consult the Report - chapter 1 • par. 1.1.1
            </button>
          </a>
        </div>
      </div>
    </div>,
    document.body
  );
}